import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  fetchReviewList,
  type QuebonReviewListResponse,
} from '@/api/quebonReviews'
import { useHashScroll } from '@/hooks/useHashScroll'
import ReviewHero from './reviews/ReviewHero'
import BestReviewMarquee from './reviews/BestReviewMarquee'
import ReviewVideoSection from './reviews/ReviewVideoSection'
import ReviewList from './reviews/ReviewList'
import ReviewDetailDialog from './reviews/ReviewDetailDialog'

const EMPTY: QuebonReviewListResponse = {
  page: 1,
  totalPages: 1,
  best: [],
  list: [],
}

export default function ReviewsPage() {
  const { t } = useTranslation('common')
  const [reviews, setReviews] = useState<QuebonReviewListResponse>(EMPTY)
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  useHashScroll()

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 767)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const count = isMobile ? 3 : 9

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchReviewList(1, count)
      .then((res) => {
        if (!cancelled) {
          setReviews({
            page: 1,
            totalPages: res.totalPages,
            best: res.best,
            list: res.list,
          })
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          console.error(e)
          toast.error(t('reviews.loadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [count, t])

  const handleLoadMore = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      const nextPage = reviews.page + 1
      const res = await fetchReviewList(nextPage, count)
      setReviews((prev) => ({
        page: nextPage,
        totalPages: res.totalPages,
        best: res.best,
        list: [...prev.list, ...res.list],
      }))
    } catch (e) {
      console.error(e)
      toast.error(t('reviews.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [loading, reviews.page, count, t])

  return (
    <div className="text-[#131313]">
      <ReviewHero />
      <BestReviewMarquee items={reviews.best} onSelect={setSelectedId} />
      <ReviewVideoSection />
      <ReviewList
        items={reviews.list}
        page={reviews.page}
        totalPages={reviews.totalPages}
        loading={loading}
        onLoadMore={handleLoadMore}
        onSelect={setSelectedId}
      />
      <ReviewDetailDialog
        reviewId={selectedId}
        open={selectedId != null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  )
}
